// Phase 36 Batch K — RoleDot: tiny color indicator + tooltip that
// surfaces a deal row's analysis perspective in any list/card view.
//
// Visual contract:
//   • Buyer view → indigo dot (matches the DealDetail RolePill)
//   • Seller view → amber dot
// Hover tooltip reads the row's `metrics_at_role` block:
//   "Buyer view — evaluated at counter $270,000"
//   "Seller view — evaluated at asking $300,000"
//
// Single dot + tooltip on purpose (per Batch K spec) — a full chip per
// row would be too noisy in lists with 20+ rows.

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

function _fmtMoney(n) {
  if (n == null) return "—";
  const v = Math.round(Number(n) || 0);
  return `$${v.toLocaleString()}`;
}

export default function RoleDot({ metrics, role: roleProp, size = "sm", testid }) {
  // Accept either a `metrics_at_role` block or a raw deal-shaped fallback.
  const m = metrics || {};
  const role = (m.role || roleProp || "buyer").toLowerCase();
  const isSeller = role === "seller";
  const basis = m.price_basis;
  const value = m.price_basis_value;
  const sideLabel = isSeller ? "Seller view" : "Buyer view";
  let priceLabel;
  if (basis === "counter_offer") priceLabel = `evaluated at counter ${_fmtMoney(value)}`;
  else if (basis === "asking_seller_view") priceLabel = `evaluated at asking ${_fmtMoney(value)}`;
  else if (basis === "asking") priceLabel = `evaluated at asking ${_fmtMoney(value)}`;
  else priceLabel = "evaluated at asking";

  const dotSize = size === "lg" ? "h-2.5 w-2.5" : "h-1.5 w-1.5";
  const dotColor = isSeller ? "bg-amber-500" : "bg-indigo-500";
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            data-testid={testid || "role-dot"}
            data-role={role}
            data-basis={basis || "asking"}
            className={`inline-block flex-shrink-0 rounded-full ${dotSize} ${dotColor}`}
            aria-label={`${sideLabel} — ${priceLabel}`}
          />
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          <span className="font-semibold">{sideLabel}</span> — {priceLabel}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
